import React from 'react';
export function UnitArt({ type = 'Melee', stage = 0, line = 1, portrait = false }: {
    type?: string;
    stage?: number;
    line?: number;
    portrait?: boolean;
}) {
    const index = Math.max(0, Math.min(19, line - 1));
    // The generated atlas has uneven row spacing; use measured cell bounds.
    const [rowY, rowHeight] = [[0, 280], [280, 275], [555, 245], [800, 280], [1080, 322]][Math.floor(index / 4)];
    return <span className={`${portrait ? 'unit-portrait' : 'unit-art'} sprite-companion pixel-art stage-${stage}`} aria-hidden="true" style={{ '--portrait-ratio': 280.5 / rowHeight, backgroundImage: "url('/art/companions-pixel.png')", backgroundSize: `400% ${1402 / rowHeight * 100}%`, backgroundPosition: `${index % 4 / 3 * 100}% ${rowY / (1402 - rowHeight) * 100}%` } as React.CSSProperties}>{stage > 0 && <span className="stage-stars">{stage === 1 ? '◆' : '◆◆'}</span>}</span>;
}
// Mount once per battlefield ally; UI rerenders and evolution do not replay arrival.
export function AllyArrival({ children }: { children: React.ReactNode }) {
    const [arriving, setArriving] = React.useState(true);
    React.useEffect(() => {
        const timer = window.setTimeout(() => setArriving(false), 1400);
        return () => window.clearTimeout(timer);
    }, []);
    return <span className={`ally-arrival${arriving ? ' arriving' : ''}`} aria-hidden="true">
        {arriving && <span className="ally-spawn-vfx"><span className="ally-spawn-ring"/>{Array.from({ length: 6 }, (_, i) => <i key={i} style={{ '--spark-angle': `${i * 60}deg` } as React.CSSProperties}/>)}</span>}
        <span className="ally-arrival-sprite">{children}</span>
    </span>;
}
export function EnemyArt({ boss = false, type = 'Melee', floor = 1, def }: {
    boss?: boolean;
    type?: string;
    floor?: number;
    def?: string;
}) { const index = def?.startsWith('E') ? Number(def.slice(1)) - 1 : def === 'B12X' ? 23 : 12 + (def ? Number(def.slice(1)) - 1 : floor - 1); return <span className={`enemy-art sprite-enemy ${def === 'B12X' ? 'ex-king' : ''}`} aria-hidden="true" style={{ backgroundImage: "url('/art/enemies-pixel.png')", backgroundPosition: `${index % 5 * 25}% ${Math.floor(index / 5) * 25}%` }}/>; }
export function Crown() { return <svg viewBox="0 0 32 24" shapeRendering="crispEdges" aria-hidden="true"><path d="M3 6h2v3h3v3h3V8h2V5h2V2h2v3h2v3h2v4h3V9h3V6h2v14H3zM5 22h22v2H5z" fill="currentColor"/><path d="M14 12h4v5h-4z" fill="#2b4c42"/></svg>; }
const speakerIndex: Record<string, number> = { Aster: 0, Mira: 1, Ilyra: 2, Veyr: 3, 'EX King': 4, 'Previous Self': 5, Guardian: 6, 'Moon Witch': 7, 'Crown General': 8 };
export function SpeakerPortrait({ speaker }: {
    speaker: string;
}) {
    const index = speakerIndex[speaker] ?? 0;
    // Measured row boundaries keep the transparent busts inside their atlas cells.
    const [y, height] = [[0, 414], [414, 390], [804, 450]][Math.floor(index / 3)];
    return <div className="speaker-portrait" role="img" aria-label={`${speaker}, illustrated dialogue portrait`} style={{ backgroundSize: `300% ${1254 / height * 100}%`, backgroundPosition: `${index % 3 * 50}% ${y / (1254 - height) * 100}%` }}/>;
}

const relicScenes: Record<string, number> = {
 'FX-I01':0, 'FX-I02':14, 'FX-I03':4, 'FX-I04':3, 'FX-I05':7, 'FX-I06':8, 'FX-I07':11, 'FX-I08':5,
 'FX-L01':1, 'FX-L02':8, 'FX-L03':3, 'FX-L04':2, 'FX-L05':6, 'FX-L06':8, 'FX-L07':15, 'FX-L08':10, 'FX-L09':7, 'FX-L10':15,
 'FX-G01':1, 'FX-G02':14, 'FX-G03':14, 'FX-G04':0, 'FX-G05':6, 'FX-G06':2, 'FX-G07':9, 'FX-G08':15, 'FX-G09':13, 'FX-G10':7,
 'FX-P01':3, 'FX-P02':8, 'FX-P03':14, 'FX-P04':1, 'FX-P05':10, 'FX-T01':12, 'FX-T02':9, 'FX-T03':13, 'FX-T04':12,
};
export function RelicArt({ id }: { id: string }) {
    const index = relicScenes[id] ?? 12;
    return <span className="relic-illustration" aria-hidden="true" style={{ backgroundImage: "url('/art/relic-illustrations.png')", backgroundPosition: `${index % 4 * 100 / 3}% ${Math.floor(index / 4) * 100 / 3}%` }}/>;
}

export function objectiveScene(floor: number, name: string, index = 0) {
    if (name.toLowerCase().includes('ore')) return 12;
    if (name.toLowerCase().includes('cage')) return 4;
    if (name.toLowerCase().includes('optional')) return 13;
    if (floor === 10) return index === 2 ? 15 : 9;
    return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 10, 11][floor - 1] ?? 13;
}
export function ObjectiveArt({ floor, name, index = 0 }: { floor: number; name: string; index?: number }) {
    const cell = objectiveScene(floor, name, index);
    return <span className={`objective-art objective-floor-${floor}`} aria-hidden="true" style={{ backgroundImage: "url('/art/objectives-pixel.png')", backgroundPosition: `${cell % 4 * 100 / 3}% ${Math.floor(cell / 4) * 100 / 3}%`, filter: floor === 4 ? `hue-rotate(${index * 95}deg)` : undefined }}/>;
}
